// ===== FUNCTIONS PURAS (para tree-shaking máximo) =====

import { AsYouType } from 'libphonenumber-js/min';

/**
 * Remove todos os caracteres não numéricos
 */
export function apenasNumeros(texto: string): string {
  if (!texto) return '';
  return texto.replace(/\D/g, '');
}

/**
 * Remove máscara de qualquer formato
 */
export function removerMascara(texto: string): string {
  return apenasNumeros(texto);
}

/**
 * Formata CPF: 000.000.000-00
 */
export function formatarCPF(cpf: string): string {
  const numeros = apenasNumeros(cpf);
  if (numeros.length !== 11) return cpf;
  return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Formata CNPJ: 00.000.000/0000-00
 */
export function formatarCNPJ(cnpj: string): string {
  const numeros = apenasNumeros(cnpj);
  if (numeros.length !== 14) return cnpj;
  return numeros.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    '$1.$2.$3/$4-$5'
  );
}

/**
 * Formata telefone nacional brasileiro ou internacional com DDI.
 * Ex.: 11999887766 → (11) 99988-7766; 5511999887766 → +55 11 99988 7766;
 * 64210574749 → +64 210 574 749.
 */
export function formatarTelefone(telefone: string): string {
  const numeros = apenasNumeros(telefone);
  if (!numeros) return telefone ?? '';

  const internacional = _formatarInternacional(numeros);
  if (internacional) return internacional;

  if (numeros.length === 11) {
    return numeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (numeros.length === 10) {
    return numeros.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }

  return telefone;
}

/**
 * Mascara telefone para exibição, ocultando parte do número: (00) 00***-0000
 */
export function mascararTelefone(telefone: string): string {
  const numeros = apenasNumeros(telefone);
  if (!numeros) return telefone ?? '';

  const internacional = _mascararInternacional(numeros);
  if (internacional) return internacional;

  if (numeros.length === 11) {
    return numeros.replace(/(\d{2})(\d{2})\d{3}(\d{4})/, '($1) $2***-$3');
  }
  if (numeros.length === 10) {
    return numeros.replace(/(\d{2})(\d{2})\d{2}(\d{4})/, '($1) $2***-$3');
  }

  return telefone;
}

/**
 * Celular BR sem DDI tem 11 dígitos e o nono dígito (posição 3).
 * Fixo BR tem 10 dígitos e o assinante começa em 2–5.
 * Fora disso, um 64 de 11 dígitos é Nova Zelândia, não DDD.
 */
function _pareceTelefoneNacionalBr(numeros: string): boolean {
  if (numeros.length === 11) return numeros[2] === '9';
  if (numeros.length === 10) return numeros[2] >= '2' && numeros[2] <= '5';
  return false;
}

function _formatarInternacional(numeros: string): string | null {
  if (_pareceTelefoneNacionalBr(numeros)) return null;
  try {
    const formatter = new AsYouType();
    const formatted = formatter.input(`+${numeros}`);
    if (!formatter.isValid() || !formatter.getCountry()) return null;
    return formatted;
  } catch {
    return null;
  }
}

function _mascararInternacional(numeros: string): string | null {
  if (_pareceTelefoneNacionalBr(numeros)) return null;
  try {
    const formatter = new AsYouType();
    formatter.input(`+${numeros}`);
    const calling = formatter.getCallingCode();
    if (!formatter.isValid() || !calling || !numeros.startsWith(calling)) {
      return null;
    }
    const national = numeros.slice(calling.length);
    const fim = national.slice(-4);
    const inicio = national.length > 6 ? national.slice(0, 2) : '';
    return `+${calling} ${inicio}***${fim}`.trim();
  } catch {
    return null;
  }
}

/**
 * Mascara email para exibição, ocultando parte do usuário: jo***@dominio.com
 */
export function mascararEmail(email: string): string {
  const [usuario, dominio] = email.split('@');
  if (!usuario || !dominio) return email;

  const prefixo = usuario.length <= 2 ? usuario.slice(0, 1) : usuario.slice(0, 2);
  return `${prefixo}***@${dominio}`;
}

/**
 * Formata CEP: 00000-000
 */
export function formatarCEP(cep: string): string {
  const numeros = apenasNumeros(cep);
  if (numeros.length !== 8) return cep;
  return numeros.replace(/(\d{5})(\d{3})/, '$1-$2');
}

/**
 * Formata moeda brasileira: R$ 1.234,56
 */
export function formatarMoeda(valor: number | string): string {
  const numero = typeof valor === 'string' ? parseFloat(valor) : valor;
  if (isNaN(numero)) return 'R$ 0,00';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numero);
}

/**
 * Formata porcentagem: 12,5%
 */
export function formatarPorcentagem(valor: number): string {
  if (isNaN(valor)) return '0%';

  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(valor / 100);
}

/**
 * Formata RG: 00.000.000-0
 */
export function formatarRG(rg: string): string {
  const texto = rg.replace(/[^0-9X]/g, ''); // Remove tudo exceto números e X
  if (texto.length !== 9) return rg;
  return texto.replace(/(\d{2})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4');
}

/**
 * Formata cartão de crédito: 0000 0000 0000 0000
 */
export function formatarCartaoCredito(cartao: string): string {
  const numeros = apenasNumeros(cartao);
  return numeros.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

/**
 * Formata número com separadores de milhares: 1.234.567
 */
export function formatarNumero(numero: number | string): string {
  const num = typeof numero === 'string' ? parseFloat(numero) : numero;
  if (isNaN(num)) return '0';

  return new Intl.NumberFormat('pt-BR').format(num);
}

/**
 * Formata código de barras: 00000.00000 00000.000000 00000.000000 0 00000000000000
 */
export function formatarCodigoBarras(codigo: string): string {
  const numeros = apenasNumeros(codigo);
  if (numeros.length !== 44) return codigo;

  return numeros.replace(
    /(\d{5})(\d{5})(\d{5})(\d{6})(\d{5})(\d{6})(\d{1})(\d{14})/,
    '$1.$2 $3.$4 $5.$6 $7 $8'
  );
}

/**
 * Formata placa de veículo: ABC-0000 ou ABC0A00 (Mercosul)
 */
export function formatarPlaca(placa: string): string {
  const texto = placa.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

  // Placa antiga: AAA0000
  if (/^[A-Z]{3}\d{4}$/.test(texto)) {
    return texto.replace(/([A-Z]{3})(\d{4})/, '$1-$2');
  }

  // Placa Mercosul: AAA0A00
  if (/^[A-Z]{3}\d{1}[A-Z]{1}\d{2}$/.test(texto)) {
    return texto.replace(/([A-Z]{3})(\d{1}[A-Z]{1}\d{2})/, '$1-$2');
  }

  return placa;
}

/**
 * Formata PIS/PASEP: 000.00000.00-0
 */
export function formatarPIS(pis: string): string {
  const numeros = apenasNumeros(pis);
  if (numeros.length !== 11) return pis;
  return numeros.replace(/(\d{3})(\d{5})(\d{2})(\d{1})/, '$1.$2.$3-$4');
}

/**
 * Formata número decimal brasileiro: 1.234,56
 */
export function formatarDecimal(
  numero: number | string,
  casasDecimais = 2
): string {
  const num = typeof numero === 'string' ? parseFloat(numero) : numero;
  if (isNaN(num))
    return '0' + (casasDecimais > 0 ? ',' + '0'.repeat(casasDecimais) : '');

  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: casasDecimais,
    maximumFractionDigits: casasDecimais,
  }).format(num);
}

/**
 * Formata data no momento da digitação: DD/MM/AAAA
 */
export function formatarData(data: string): string {
  let valor = apenasNumeros(data);

  // Limita o tamanho a 8 caracteres numéricos (DDMMAAAA)
  if (valor.length > 8) {
    valor = valor.substring(0, 8);
  }

  if (valor.length > 2) {
    valor = valor.substring(0, 2) + '/' + valor.substring(2);
  }
  if (valor.length > 5) {
    valor = valor.substring(0, 5) + '/' + valor.substring(5, 9);
  }

  return valor;
}

// ===== CLASSE ESTÁTICA (para organização e compatibilidade) =====

/**
 * Classe estática com todos os utilitários de máscara
 * Use quando preferir organização por namespace: MaskUtils.formatarCPF()
 */
export class MaskUtils {
  static apenasNumeros = apenasNumeros;
  static removerMascara = removerMascara;
  static formatarCPF = formatarCPF;
  static formatarCNPJ = formatarCNPJ;
  static formatarTelefone = formatarTelefone;
  static mascararTelefone = mascararTelefone;
  static mascararEmail = mascararEmail;
  static formatarCEP = formatarCEP;
  static formatarMoeda = formatarMoeda;
  static formatarPorcentagem = formatarPorcentagem;
  static formatarRG = formatarRG;
  static formatarCartaoCredito = formatarCartaoCredito;
  static formatarNumero = formatarNumero;
  static formatarCodigoBarras = formatarCodigoBarras;
  static formatarPlaca = formatarPlaca;
  static formatarPIS = formatarPIS;
  static formatarDecimal = formatarDecimal;
  static formatarData = formatarData;
}
